import { useState } from "react";
import type { TreeMapPoint } from "@/types/cta-mapa-ui";
import { classesRiscoOptions, riscoLabel, tipoIndividuoLabel } from "@/config/cta-mapa-config";
import { riscoConfig } from "@/config/cta-config";

interface PilotTreeListProps {
  arvores: TreeMapPoint[];
  loaded: boolean;
  onSelectTree: (tree: TreeMapPoint) => void;
}

function formatDate(value: string | null): string {
  if (!value) return "Sem vistoria";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

/** Exemplares cadastrados no projeto da area piloto (dado real do CTA). */
export default function PilotTreeList({ arvores, loaded, onSelectTree }: PilotTreeListProps) {
  const [filterRisco, setFilterRisco] = useState("");
  const [filterEspecie, setFilterEspecie] = useState("");

  const especies = Array.from(new Set(arvores.map((a) => a.nomePopular).filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt-BR"));

  const filtered = arvores.filter((a) => {
    if (filterRisco) {
      if (filterRisco === "SEM_AVALIACAO" ? a.classeRisco !== "" : a.classeRisco !== filterRisco) return false;
    }
    if (filterEspecie && a.nomePopular !== filterEspecie) return false;
    return true;
  });

  const selectClass =
    "text-xs border border-background-200 rounded-md px-2 py-1 bg-white text-foreground-700 focus:outline-none focus:border-accent-400";

  return (
    <div className="bg-white rounded-card border border-background-200/70 overflow-hidden flex flex-col h-full">
      <div className="px-4 py-3 border-b border-background-200/70">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-plant-line text-primary-500 text-sm"></i>
          <h3 className="text-sm font-semibold text-foreground-800 font-heading">Exemplares cadastrados</h3>
          <span className="text-[10px] font-medium text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
            {filtered.length} de {arvores.length}
          </span>
        </div>
        {arvores.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <select value={filterRisco} onChange={(e) => setFilterRisco(e.target.value)} className={selectClass}>
              <option value="">Todos os riscos</option>
              {classesRiscoOptions.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
              <option value="SEM_AVALIACAO">Sem avaliação</option>
            </select>
            {especies.length > 0 && (
              <select value={filterEspecie} onChange={(e) => setFilterEspecie(e.target.value)} className={selectClass}>
                <option value="">Todas as espécies</option>
                {especies.map((e) => (
                  <option key={e} value={e}>{e}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="overflow-auto flex-1 divide-y divide-background-200/50">
        {filtered.map((a) => {
          const badge = a.classeRisco ? riscoConfig[a.classeRisco]?.badge ?? "bg-background-100 text-foreground-500" : "bg-background-100 text-foreground-400";
          return (
            <button
              key={a.id}
              onClick={() => onSelectTree(a)}
              className="w-full text-left px-4 py-2 hover:bg-background-50 transition-colors flex items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-foreground-800 truncate">{a.nomePopular || "Espécie não identificada"}</p>
                <p className="text-[10px] text-foreground-500 font-mono truncate">
                  {a.codigoCta} · {tipoIndividuoLabel(a.tipo)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${badge}`}>{riscoLabel(a.classeRisco)}</span>
                <p className="text-[9px] text-foreground-400 mt-0.5">{formatDate(a.ultimaVistoria)}</p>
              </div>
            </button>
          );
        })}

        {filtered.length === 0 && (
          <div className="p-6 text-center">
            <p className="text-xs text-foreground-500">
              {!loaded
                ? "Carregando exemplares…"
                : arvores.length === 0
                  ? "Nenhum exemplar cadastrado no projeto desta área piloto."
                  : "Nenhum exemplar com os filtros atuais."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
